import { Button } from "@msuite/ui/button";
import { useDisclosure, useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { PDFViewer } from "@msuite/ui/pdf-viewer";
import { createFile, download } from "@msuite/utils/files";
import type { Nullable, Tables } from "@msuite/utils/types";
import { Fragment, useState } from "react";
import { getRemoteFileUrl } from "@/api/files";

/** Props Interface */
interface ReturnOrderSheetActionsProps {
	file: Tables<"files">;
}

/**
 * Read the sheet, or save it — the two things anybody does with one.
 *
 * Nothing here removes a sheet. Replacing one is the server's work, on every write that moves the
 * run's fleet — the file offered below is always the current document.
 *
 * Viewing is the common case and downloading the deliberate one, so the eye sits first. Saving
 * fetches the bytes rather than opening a tab at the URL: the sheet's job is to be printed and put
 * in a cab, and a `window.open` after an awaited fetch is a popup the browser may block, because
 * the click that authorised it is over by the time the URL resolves.
 */
export function ReturnOrderSheetActions({ file }: ReturnOrderSheetActionsProps) {
	/** Hooks */
	const viewer = useDisclosure();
	const viewLoader = useLoading();
	const downloadLoader = useLoading();

	/** State */
	const [signedUrl, setSignedUrl] = useState<Nullable<string>>(null);

	/** Functions */
	// Resolved per press rather than per row: storage URLs are signed and expire, so a table that
	// fetched one for every row would spend them on sheets nobody opens, and hold a stale one for
	// the reader who comes back to the tab an hour later.
	async function handleOnView() {
		try {
			viewLoader.start();
			const url = await getRemoteFileUrl(file);
			if (!url) return;
			setSignedUrl(url);
			viewer.onOpen();
		} catch (error) {
			console.error("Error opening return order sheet:", error);
		} finally {
			viewLoader.stop();
		}
	}

	async function handleOnDownload() {
		try {
			downloadLoader.start();
			const url = await getRemoteFileUrl(file);
			if (!url) return;
			await download(await createFile(url, file.fileName, file.contentType));
		} catch (error) {
			console.error("Error downloading return order sheet:", error);
		} finally {
			downloadLoader.stop();
		}
	}

	/** Render */
	return (
		<Fragment>
			<Button
				size="xxs"
				variant="ghost"
				icon={<Icon name="Eye" />}
				onClick={handleOnView}
				isLoading={viewLoader.isLoading}
			/>
			<Button
				size="xxs"
				variant="ghost"
				icon={<Icon name="Download" />}
				onClick={handleOnDownload}
				isLoading={downloadLoader.isLoading}
			/>
			{viewer.isOpen && !!signedUrl && (
				<PDFViewer
					isOpen={viewer.isOpen}
					onClose={viewer.onClose}
					url={signedUrl}
				/>
			)}
		</Fragment>
	);
}
